import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle } from "lucide-react";

import { resetPasswordWithToken } from "@/lib/auth/auth.api";
import { getApiErrorMessage } from "@/utils/apiErrorMessage";
import { MarketingSiteLink } from "@/pages/auth/MarketingSiteLink";
import {
  AuthCard,
  AuthPageShell,
  authFieldClass,
  authLinkClass,
  authPrimaryButtonClass,
} from "@/pages/auth/AuthPageShell";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get("token")?.trim() ?? "", [searchParams]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError("Reset link is missing a token. Open the link from your email.");
      return;
    }
    if (password.length < 8) {
      setError("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await resetPasswordWithToken({ token, newPassword: password });
      setDone(true);
      window.setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { code?: string; message?: string }; status?: number };
      };
      const st = ax?.response?.status;
      const data = ax?.response?.data;
      if (st === 404 || st === 503) {
        setError("Password reset is not available on this server yet. Try again later.");
      } else {
        setError(getApiErrorMessage(data) || "Could not reset password.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthPageShell>
      <AuthCard>
        {done ? (
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle className="h-6 w-6 text-emerald-600" aria-hidden />
            </div>
            <h1 className="mt-4 text-lg font-medium text-slate-900">Password updated</h1>
            <p className="mt-2 text-sm text-slate-500">Redirecting you to sign in…</p>
            <p className="mt-4 text-sm">
              <Link to="/login" className={authLinkClass}>
                Go to sign in now
              </Link>
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-lg font-medium text-slate-900">Choose a new password</h1>
            <p className="mt-2 text-sm text-slate-500">Enter a new password for your account.</p>

            {error ? (
              <div
                className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                role="alert"
              >
                {error}
              </div>
            ) : null}

            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="reset-password" className="block text-sm font-medium text-slate-700">
                  New password
                </label>
                <input
                  id="reset-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(ev) => setPassword(ev.target.value)}
                  className={`mt-1 w-full ${authFieldClass}`}
                />
              </div>
              <div>
                <label
                  htmlFor="reset-password-confirm"
                  className="block text-sm font-medium text-slate-700"
                >
                  Confirm password
                </label>
                <input
                  id="reset-password-confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(ev) => setConfirm(ev.target.value)}
                  className={`mt-1 w-full ${authFieldClass}`}
                />
              </div>
              <button
                type="submit"
                disabled={submitting || !token}
                className={authPrimaryButtonClass}
              >
                {submitting ? "Saving…" : "Update password"}
              </button>
            </form>
          </>
        )}

        {!done ? (
          <p className="mt-6 text-center text-sm text-slate-600">
            <Link to="/login" className={authLinkClass}>
              Back to sign in
            </Link>
          </p>
        ) : null}
        <p className="mt-3 text-center">
          <MarketingSiteLink />
        </p>
      </AuthCard>
    </AuthPageShell>
  );
}
